import React, { useState, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { ChevronLeft, Plus, Loader2, FileText, Upload, XCircle, ExternalLink } from 'lucide-react';
import { JobNote } from '../../types/workOrders';
import { useAuthStore } from '../../store/authStore';
import { API_BASE } from '../../utils/api';
import { toast } from 'react-hot-toast';

interface JobNotesPageProps {
  reportId: number;
  woNumber: string;
  onBack: () => void;
  notes: JobNote[];
  onNoteAdded: (note: JobNote) => void;
}

export function JobNotesPage({ 
  reportId, 
  woNumber, 
  onBack,
  notes,
  onNoteAdded 
}: JobNotesPageProps) {
  const [newNote, setNewNote] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const { accessToken } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (selectedFiles.length + files.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    // Check each file size
    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error('Each file must be less than 10MB');
      return;
    }

    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim() || !accessToken) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('note_text', newNote.trim());
      formData.append('report_id', reportId.toString());
      
      if (selectedFiles.length > 0) {
        selectedFiles.forEach(file => {
          formData.append('files', file);
        });
      }

      const response = await fetch(`${API_BASE}/job-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to add note');
      }

      const note = await response.json();
      onNoteAdded({
        ...note,
        createdAt: new Date().toISOString() // Ensure we have a valid date string
      });
      setNewNote('');
      setSelectedFiles([]);
      toast.success('Note added successfully');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Sort notes by creation date (newest first)
  const sortedNotes = [...notes].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors"
        >
          <ChevronLeft className="h-6 w-6 text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Job Notes
          </h2>
          <p className="text-gray-600 dark:text-gray-400">WO #{woNumber}</p>
        </div>
      </div>

      {/* Add Note Form */}
      <div className="bg-light-card dark:bg-dark-card rounded-lg p-6 border border-light-border dark:border-dark-border">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="note" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Add New Note
            </label>
            <textarea
              id="note"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note here..."
              className="w-full rounded-lg border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card text-gray-900 dark:text-gray-100 p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              disabled={loading}
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Attachments
            </label>
            <div className="relative w-full">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                multiple
                disabled={loading}
              />
              <div className="space-y-3">
                {selectedFiles.length > 0 && (
                  <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/20 rounded-lg p-3 w-full">
                    <div className="flex flex-col gap-2 w-full">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2 truncate">
                            <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                              {file.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedFiles(files => files.filter((_, i) => i !== index));
                            }}
                            className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedFiles.length < 5 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                    disabled={loading}
                  >
                    <Upload className="h-5 w-5" />
                    <span>Click to upload files</span>
                  </button>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Max file size: 10MB per file. Up to 5 files. Supported formats: Images, PDF, DOC, DOCX, XLS, XLSX
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading || !newNote.trim()}
                className="btn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Add Note
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Notes List */}
      <div className="space-y-4">
        {sortedNotes.map((note) => (
          <div
            key={`${note.id}-${note.createdAt}`}
            className="bg-light-card dark:bg-dark-card rounded-lg p-6 border border-light-border dark:border-dark-border"
          >
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <p className="text-gray-900 dark:text-gray-100">{note.note_text}</p>
                <div className="mt-2 flex items-center gap-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ID: {note.id}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(note.createdAt)}
                  </span>
                </div>
                {note.files?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      Attachments:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {note.files.map((file) => (
                        <a
                          key={file.id}
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        >
                          <FileText className="h-4 w-4" />
                          <span className="truncate max-w-[150px]">{file.fileName}</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {sortedNotes.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No notes available
          </div>
        )}
      </div>
    </div>
  );
}